// components/PieChart.tsx
import { Spinner, SpinnerSize } from '@fluentui/react';
import { ClassNamesFromIStyles, IH2OTheme, useClassNames } from '@h2oai/ui-kit';
import {
  ActiveElement,
  ArcElement,
  Chart as ChartJS,
  ChartData,
  ChartEvent,
  ChartOptions,
  Legend,
  Tooltip,
  TooltipItem,
  TooltipModel,
} from 'chart.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';

import { IPieChartStyles, pieChartStylesDefault } from './PieChart.styles';

ChartJS.register(ArcElement, Tooltip, Legend);

interface IPieChartProps {
  data?: ChartData<'pie'> | null;
  options: ChartOptions<'pie'>;
  pieChartStyles?: (theme: IH2OTheme) => Partial<IPieChartStyles>;
  loading?: boolean;
  error?: boolean;
  maxSlices?: number;
}

interface CustomTooltipData {
  label: string;
  value: number;
}

type CustomPieDataset = ChartData<'pie'>['datasets'][number] & {
  _otherCount?: number;
};

const emptyData: ChartData<'pie'> = {
  labels: [],
  datasets: [
    {
      data: [],
      backgroundColor: ['rgba(0, 0, 0, 0)'],
    },
  ],
};

const PieChart = ({
  data,
  options,
  pieChartStyles,
  loading = false,
  error = false,
  maxSlices = 10,
}: IPieChartProps) => {
  const classNames = useClassNames<IPieChartStyles, ClassNamesFromIStyles<IPieChartStyles>>(
    'pie-chart',
    pieChartStyles || pieChartStylesDefault
  );

  const chartRef = useRef<ChartJSOrUndefined<'pie'>>(null);
  const [showOtherDetails, setShowOtherDetails] = useState(false);
  const [otherDetailsPosition, setOtherDetailsPosition] = useState({ x: 0, y: 0 });
  const [otherData, setOtherData] = useState<CustomTooltipData[]>([]);

  const transformedData = useMemo(() => {
    if (!data || !data.datasets.length) {
      return {
        chartData: emptyData,
        otherTooltipData: [],
      };
    }

    const dataset = data.datasets[0];
    if (!dataset.data || dataset.data.length <= maxSlices) {
      return {
        chartData: data,
        otherTooltipData: [],
      };
    }

    const indexedData = dataset.data
      .map((value, index) => ({ index, value: value as number }))
      .sort((a, b) => b.value - a.value);

    const topSlices = indexedData.slice(0, maxSlices - 1);
    const otherSlices = indexedData.slice(maxSlices - 1);
    const otherValue = otherSlices.reduce((sum, slice) => sum + slice.value, 0);

    const newLabels = [...topSlices.map((slice) => data.labels?.[slice.index] || ''), 'Other'];

    const newData = [...topSlices.map((slice) => slice.value), otherValue];

    const newBackgroundColors = [
      ...topSlices.map((slice) => {
        if (Array.isArray(dataset.backgroundColor)) {
          return dataset.backgroundColor[slice.index] || '#CCCCCC';
        }
        return dataset.backgroundColor || '#CCCCCC';
      }),
      '#888888',
    ];

    const otherTooltipData = otherSlices.map((slice) => ({
      label: String(data.labels?.[slice.index] || ''),
      value: slice.value,
    }));

    return {
      chartData: {
        labels: newLabels,
        datasets: [
          {
            ...dataset,
            data: newData,
            backgroundColor: newBackgroundColors,
            _otherCount: otherSlices.length,
          },
        ],
      },
      otherTooltipData,
    };
  }, [data, maxSlices]);

  useEffect(() => {
    setOtherData(transformedData.otherTooltipData);
  }, [transformedData]);

  const hasData =
    data && data.datasets.length > 0 && data.datasets.some((dataset) => dataset.data && dataset.data.length > 0);

  const chartData = hasData ? transformedData.chartData : emptyData;

  const handleChartClick = useCallback(
    (event: ChartEvent, elements: ActiveElement[]) => {
      if (elements.length > 0) {
        const elementIndex = elements[0].index;
        const label = chartData.labels?.[elementIndex];

        if (label === 'Other') {
          const rect = chartRef.current?.canvas.getBoundingClientRect();
          if (rect && event.native) {
            setOtherDetailsPosition({
              x: rect.left + (event.native as MouseEvent).offsetX,
              y: rect.top + (event.native as MouseEvent).offsetY,
            });
            setShowOtherDetails(true);
          }
        }
      }
    },
    [chartData]
  );

  const chartOptions = useMemo(() => {
    const customOptions = {
      ...options,
      onClick: handleChartClick,
      plugins: {
        ...options.plugins,
        tooltip: {
          ...options.plugins?.tooltip,
          callbacks: {
            ...options.plugins?.tooltip?.callbacks,
            label: function (this: TooltipModel<'pie'>, context: TooltipItem<'pie'>) {
              const label = context.label || '';
              const value = context.raw || 0;

              if (label === 'Other') {
                const otherCount = (context.dataset as CustomPieDataset)._otherCount || 0;
                return `Other: ${otherCount} elements, click to see more`;
              }

              if (options.plugins?.tooltip?.callbacks?.label) {
                return options.plugins.tooltip.callbacks.label.call(this, context);
              }

              return `${label}: ${value}`;
            },
          },
        },
      },
    };

    return customOptions;
  }, [options, handleChartClick]);
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Element;
    if (!target.closest('.custom-tooltip')) {
      setShowOtherDetails(false);
    }
  };

  React.useEffect(() => {
    if (showOtherDetails) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showOtherDetails]);

  return (
    <div className={classNames.pieChartRoot}>
      <div className={classNames.pieChartContainer}>
        <Pie ref={chartRef} data={chartData} options={chartOptions} />

        {!hasData && !loading && !error && (
          <div className={classNames.overlayContainer}>
            <div className={classNames.overlayText}>No Data</div>
          </div>
        )}

        {loading && (
          <div className={classNames.overlayContainer}>
            <Spinner size={SpinnerSize.large} className={classNames.loadingSpinner} />
          </div>
        )}

        {error && (
          <div className={classNames.overlayContainer}>
            <div className={classNames.overlayErrorText}>An error occurred</div>
          </div>
        )}
      </div>

      {/* Other details popup */}
      {showOtherDetails && otherData.length > 0 && (
        <div
          className={`${classNames.customTooltip} custom-tooltip`}
          style={{
            position: 'fixed',
            left: otherDetailsPosition.x,
            top: otherDetailsPosition.y,
            zIndex: 1000,
          }}
        >
          <div className={classNames.tooltipHeader}>
            <span>Other Elements ({otherData.length})</span>
            <button onClick={() => setShowOtherDetails(false)} className={classNames.tooltipHeaderButton}>
              ×
            </button>
          </div>
          <div className={classNames.tooltipContent}>
            {otherData.map((item: CustomTooltipData, index: number) => (
              <div key={index} className={classNames.tooltipItem}>
                <span className={classNames.tooltipLabel}>{item.label}:</span>
                <span className={classNames.tooltipValue}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PieChart;
